import { Alert, Stack } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import adminApi from "../../../../../../api/adminApi";
import useFundraisingDetailsPageContext from "../useFundraisingDetailsPageContext";
import FundraisingDocumentsActions from "./FundraisingDocumentsActions";
import { FundraisingDocumentsContextProvider } from "./FundraisingDocumentsContext";
import FundraisingDocumentsGrid from "./FundraisingDocumentsGrid";
import type { FundraisingDocumentsState } from "./fundraisingDocumentsState";
import {
  getFilteredDocuments,
  getInitialFundraisingDocumentsState,
  startDownloadingFileAction,
  stopDownloadingFileAction,
} from "./fundraisingDocumentsState";
import FundraisingDocumentsUploadArea from "./FundraisingDocumentsUploadArea";

interface DialogState {
  openDialog?: "preview";
  fileInfo?: FileDownloadInfo;
}

const getFundraisingFileDownloadInfo = withErrorHandling(adminApi.getFundraisingFileDownloadInfo);
const getDownloadUrlForMultipleFundraisingFiles = withErrorHandling(adminApi.getDownloadUrlForMultipleFundraisingFiles);

const FundraisingDocuments = () => {
  const { sendNotificationError } = useNotificationContext();
  const { fundraising, fundraisingCategories } = useFundraisingDetailsPageContext();

  const [dialogState, setDialogState] = useState<DialogState>({});
  const [documentsState, setDocumentsState] = useState<FundraisingDocumentsState>(() =>
    getInitialFundraisingDocumentsState(fundraising.files, fundraising.fileSections)
  );

  const handlePreviewFile = async (fileId: string) => {
    setDocumentsState(startDownloadingFileAction(fileId));
    const [fileInfo, error] = await getFundraisingFileDownloadInfo(fundraising.id, fileId);
    setDocumentsState(stopDownloadingFileAction());

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[FundraisingDocuments] getFundraisingFileDownloadInfo");
      return;
    }

    setDialogState({ openDialog: "preview", fileInfo });
  };

  const handleDownloadFile = async (fileId: string) => {
    setDocumentsState(startDownloadingFileAction(fileId));
    const [fileInfo, error] = await getFundraisingFileDownloadInfo(fundraising.id, fileId);
    setDocumentsState(stopDownloadingFileAction());

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[FundraisingDocuments] getFundraisingFileDownloadInfo");
      return;
    }

    await downloadFileFromUrl(fileInfo.downloadUrl);
  };

  const handleDownloadMultipleFiles = async (fileIds: string[]) => {
    const [downloadUrl, error] = await getDownloadUrlForMultipleFundraisingFiles(
      fundraising.id,
      "fundraising_documents.zip",
      fileIds
    );

    if (error || !downloadUrl) {
      sendNotificationError(error?.message || "Failed to get download URL for the files");
      logError(error, "[FundraisingDocuments] getDownloadUrlForMultipleFundraisingFiles");
      return;
    }

    await downloadFileFromUrl(downloadUrl);
  };

  if (fundraisingCategories.length === 0) {
    return (
      <Alert severity="error">Document assignment is not possible: no Fundraising access categories defined</Alert>
    );
  }

  return (
    <FundraisingDocumentsContextProvider
      documentsState={documentsState}
      setDocumentsState={setDocumentsState}
      filteredDocuments={getFilteredDocuments(documentsState)}
      onPreviewFile={handlePreviewFile}
      onDownloadFile={handleDownloadFile}
      onDownloadMultipleFiles={handleDownloadMultipleFiles}
    >
      <Stack spacing={2} sx={{ flex: 1, pt: 2, pb: 2 }}>
        <FundraisingDocumentsUploadArea />
        <FundraisingDocumentsActions />
        <FundraisingDocumentsGrid />
      </Stack>
      {dialogState.openDialog === "preview" && dialogState.fileInfo && (
        <PreviewFileDialog
          url={dialogState.fileInfo.downloadUrl}
          fileName={dialogState.fileInfo.fileName}
          onClose={() => setDialogState({})}
        />
      )}
    </FundraisingDocumentsContextProvider>
  );
};

export default FundraisingDocuments;
